
import { useTranslation } from 'react-i18next';
import { ReactElement, useCallback } from 'react'
import { DropzoneOptions, useDropzone } from 'react-dropzone'

interface DropzoneProps extends DropzoneOptions {
  onFileUpload: (files: Array<File>, tag: number | undefined) => void
  tag: number | undefined
}

/**
 * Dropzone component
 *
 * @component
 * @param {Object} props
 * @param {String} props.accept - File types accepted by input
 * @param {Boolean} props.multiple - Multiple files upload flag
 * @param {Boolean} props.onFileUpload - Uploaded files handler
 *
 * @returns {ReactElement}
 */
const DropzoneWrapper = ({
  accept = {},
  multiple = true,
  onFileUpload,
  tag,
}: DropzoneProps): ReactElement => {
  const { t } = useTranslation('ns')
  const imagesBasePath = `${process.env.PUBLIC_URL || ''}/images`
  const isReadyToUpload = typeof tag === 'number'
  const onDrop = useCallback(
    (acceptedFiles: Array<File>) => {
      if (!isReadyToUpload) return
      onFileUpload(acceptedFiles, tag)
    },
    [isReadyToUpload, onFileUpload, tag],
  )
  const dropzoneOptions = {
    onDrop,
    multiple,
    accept,
    disabled: !isReadyToUpload,
  }
  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneOptions)

  return (
    <div
      className={`bg-primary-t-20 flex w-full max-w-140 items-center justify-center rounded-lg border-2 border-dashed border-primary px-10 py-5 ${
        isReadyToUpload ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
      }`}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <button
        type="button"
        className="flex items-center gap-2 rounded bg-blue-600 px-6 py-2 font-semibold text-white shadow transition hover:bg-blue-700"
      >
        <img src={`${imagesBasePath}/download.svg`} alt="Upload Icon" className="h-5 w-5" />
        Upload
      </button>
    </div>
  )
}

export default DropzoneWrapper
